import { handlePushNotificationAction, normalizePushData } from '~/utils/pushDestination'

/**
 * Push notifications (FCM) — native iOS/Android only.
 *
 * MANUAL SETUP (do not commit secret files):
 * - Firebase Console: iOS app (bundle ID ca.glouton.twa) + Android app (package ca.glouton.app).
 * - iOS: GoogleService-Info.plist in ios/App/App/; enable Push Notifications + Remote notifications in Xcode.
 * - Android: google-services.json in android/app/; POST_NOTIFICATIONS in AndroidManifest.xml.
 * - Upload APNs Auth Key (.p8) to Firebase Console > Cloud Messaging.
 * - Run `npx cap sync` after adding config files.
 * - Never install @capacitor/push-notifications alongside @capacitor-firebase/messaging.
 */

let listenersRegistered = false

export default defineNuxtPlugin({
  name: 'push-notifications',
  enforce: 'post',
  async setup(nuxtApp) {
    if (!import.meta.client) {
      return
    }

    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) {
      return
    }

    const [{ App }, { Browser }, { FirebaseMessaging }] = await Promise.all([
      import('@capacitor/app'),
      import('@capacitor/browser'),
      import('@capacitor-firebase/messaging'),
    ])

    const pushNotifications = usePushNotifications()
    const config = useRuntimeConfig()
    const router = useRouter()
    const userStore = useUserStore()

    async function onNotificationData(
      rawData: Record<string, unknown> | undefined | null,
      eventType: 'opened' | 'received_foreground' = 'opened',
    ): Promise<void> {
      const installationUuid = await pushNotifications.getInstallationUuid()

      await handlePushNotificationAction(rawData, {
        apiUrl: config.public.apiUrl,
        installationUuid,
        eventType,
        navigate: async (target) => {
          await router.push(target)
        },
        openExternalUrl: async (url) => {
          await Browser.open({ url })
        },
      })
    }

    async function registerListeners(): Promise<void> {
      if (listenersRegistered) {
        return
      }

      listenersRegistered = true

      await FirebaseMessaging.addListener('tokenReceived', async (event) => {
        if (!event.token || !pushNotifications.pushOptIn.value) {
          return
        }

        try {
          await pushNotifications.registerDevice(event.token)
        }
        catch (error) {
          console.warn('Failed to register refreshed FCM token:', error)
        }
      })

      await FirebaseMessaging.addListener('notificationReceived', async (event) => {
        const rawData = event.notification?.data as Record<string, unknown> | undefined

        if (normalizePushData(rawData).tracking_token) {
          const installationUuid = await pushNotifications.getInstallationUuid()

          await handlePushNotificationAction(rawData, {
            apiUrl: config.public.apiUrl,
            installationUuid,
            eventType: 'received_foreground',
          })
        }
      })

      await FirebaseMessaging.addListener('notificationActionPerformed', async (event) => {
        await onNotificationData(
          event.notification?.data as Record<string, unknown> | undefined,
          'opened',
        )
      })

      await App.addListener('resume', async () => {
        const previousStatus = pushNotifications.permissionStatus.value

        await pushNotifications.checkPermission()

        if (previousStatus !== 'granted' && pushNotifications.permissionStatus.value === 'granted') {
          await pushNotifications.registerInstallation(pushNotifications.permissionStatus.value)
          await pushNotifications.syncFcmToken()
          await pushNotifications.fetchPreferences()
        }
      })
    }

    void registerListeners()

    nuxtApp.hook('app:mounted', async () => {
      await pushNotifications.initialize()
    })

    watch(
      () => userStore.access_token,
      async (token, previousToken) => {
        if (!token && !previousToken) {
          return
        }

        if (token && token !== previousToken) {
          await pushNotifications.registerInstallation()
          await pushNotifications.syncFcmToken()
          await pushNotifications.fetchPreferences()
          return
        }

        if (!token && previousToken) {
          await pushNotifications.handleUserAuthChange(false)
        }
      },
    )

    return {
      provide: {
        pushNotifications,
      },
    }
  },
})
